import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import {
  MessageSquare, Plus, ChevronRight, Clock, CheckCircle,
  X, Send, ArrowLeft, User, Shield, ChevronDown, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pendente:   { label: "Pendente",   cls: "bg-yellow-100 text-yellow-800", dot: "bg-yellow-400" },
  em_analise: { label: "Em Análise", cls: "bg-blue-100 text-blue-800",     dot: "bg-blue-400" },
  respondido: { label: "Respondido", cls: "bg-green-100 text-green-800",   dot: "bg-green-400" },
  encerrado:  { label: "Encerrado",  cls: "bg-gray-100 text-gray-600",     dot: "bg-gray-400" },
};
const PRIORITY_CONFIG = {
  urgente: { label: "Urgente", cls: "bg-red-100 text-red-800 border-red-200" },
  alta:    { label: "Alta",    cls: "bg-orange-100 text-orange-800 border-orange-200" },
  media:   { label: "Média",   cls: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  baixa:   { label: "Baixa",   cls: "bg-green-100 text-green-800 border-green-200" },
};
const WOUND_LABELS = {
  ulcera_venosa: "Úlcera Venosa",
  pe_diabetico: "Pé Diabético",
  lesao_por_pressao: "Lesão por Pressão",
};

export default function InterconsultationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preWoundId = searchParams.get("woundId");
  const prePatientId = searchParams.get("patientId");

  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);

  // Thread view
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const msgEndRef = useRef(null);

  // New consultation form
  const [newConsult, setNewConsult] = useState({
    wound_id: preWoundId || "",
    patient_id: prePatientId || "",
    question: "",
    priority: "media",
    specialist_user_id: "",
  });
  const [patients, setPatients] = useState([]);
  const [wounds, setWounds] = useState([]);
  const [saving, setSaving] = useState(false);

  const canRespond = ["especialista", "medico", "admin"].includes(user?.role);

  useEffect(() => {
    fetchConsultations();
    axios.get(`${API}/patients?limit=100`, { withCredentials: true })
      .then(r => setPatients(r.data.patients || [])).catch(() => {});
  }, [statusFilter]);

  useEffect(() => {
    if ((preWoundId || prePatientId) && !showModal) setShowModal(true);
  }, [preWoundId, prePatientId]);

  useEffect(() => {
    if (msgEndRef.current) msgEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchConsultations = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(
        `${API}/teleconsultations${statusFilter ? `?status=${statusFilter}` : ""}`,
        { withCredentials: true }
      );
      setConsultations(resp.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openThread = async (consult) => {
    setActiveThread(consult);
    setLoadingMsgs(true);
    try {
      const resp = await axios.get(`${API}/teleconsultations/${consult.consult_id}/messages`, { withCredentials: true });
      setMessages(resp.data || []);
    } catch { setMessages([]); }
    finally { setLoadingMsgs(false); }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !activeThread) return;
    setSending(true);
    const text = newMessage.trim();
    setNewMessage("");
    try {
      const resp = await axios.post(
        `${API}/teleconsultations/${activeThread.consult_id}/messages`,
        { content: text },
        { withCredentials: true }
      );
      setMessages(prev => [...prev, resp.data]);
      // Refresh the thread header to get updated status
      await fetchConsultations();
      const updated = await axios.get(`${API}/teleconsultations/${activeThread.consult_id}`, { withCredentials: true });
      setActiveThread(updated.data);
    } catch (e) { setNewMessage(text); console.error(e); }
    finally { setSending(false); }
  };

  const handleCloseConsult = async () => {
    if (!activeThread) return;
    try {
      await axios.patch(
        `${API}/teleconsultations/${activeThread.consult_id}/status`,
        { status: "encerrado" },
        { withCredentials: true }
      );
      setActiveThread(prev => ({ ...prev, status: "encerrado" }));
      await fetchConsultations();
    } catch (e) { console.error(e); }
  };

  const handlePatientChange = async (patientId) => {
    setNewConsult(p => ({ ...p, patient_id: patientId, wound_id: "" }));
    if (patientId) {
      try {
        const resp = await axios.get(`${API}/wounds?patient_id=${patientId}`, { withCredentials: true });
        setWounds(resp.data || []);
      } catch { setWounds([]); }
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/teleconsultations`, newConsult, { withCredentials: true });
      await fetchConsultations();
      setShowModal(false);
      setNewConsult({ wound_id: "", patient_id: "", question: "", priority: "media", specialist_user_id: "" });
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }) +
      " às " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  // ===== THREAD VIEW =====
  if (activeThread) {
    const status = STATUS_CONFIG[activeThread.status];
    return (
      <Layout>
        <div className="max-w-2xl mx-auto flex flex-col h-[calc(100vh-120px)]" data-testid="thread-view">
          {/* Thread header */}
          <div className="flex items-center gap-3 pb-4 border-b border-border mb-1 flex-shrink-0">
            <button
              onClick={() => setActiveThread(null)}
              className="p-2 rounded-lg hover:bg-secondary transition-all"
              data-testid="back-to-list-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-bold text-foreground">{activeThread.patient_name || "Paciente"}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${status?.cls}`}>{status?.label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${PRIORITY_CONFIG[activeThread.priority]?.cls}`}>
                  {PRIORITY_CONFIG[activeThread.priority]?.label}
                </span>
              </div>
              {activeThread.wound_info?.wound_type && (
                <p className="text-xs text-muted-foreground">{WOUND_LABELS[activeThread.wound_info.wound_type]} · {activeThread.wound_info?.location}</p>
              )}
            </div>
            {(canRespond || activeThread.requesting_user_id === user?.user_id) && activeThread.status !== "encerrado" && (
              <button
                onClick={handleCloseConsult}
                className="text-xs px-3 py-1.5 border border-border rounded-lg text-muted-foreground hover:bg-secondary transition-all flex-shrink-0"
                data-testid="close-consult-btn"
              >
                Encerrar
              </button>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto space-y-3 py-2 pr-1" data-testid="messages-container">
            {loadingMsgs ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-10">
                <MessageSquare className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Nenhuma mensagem ainda</p>
              </div>
            ) : (
              messages.map((msg) => {
                const isMe = msg.user_id === user?.user_id;
                const isSpecialist = msg.is_specialist_reply;
                return (
                  <div
                    key={msg.message_id}
                    className={`flex flex-col gap-1 ${isMe ? "items-end" : "items-start"}`}
                    data-testid={`message-${msg.message_id}`}
                  >
                    {/* Sender info */}
                    <div className={`flex items-center gap-1.5 ${isMe ? "flex-row-reverse" : ""}`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                        isSpecialist ? "bg-primary text-white" : "bg-secondary text-foreground"
                      }`}>
                        {isSpecialist ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                      </div>
                      <p className="text-xs text-muted-foreground font-medium">{msg.user_name}</p>
                      {isSpecialist && (
                        <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium">
                          Especialista
                        </span>
                      )}
                    </div>
                    {/* Bubble */}
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                      isMe
                        ? "bg-primary text-primary-foreground rounded-tr-sm"
                        : isSpecialist
                        ? "bg-primary/10 text-foreground border border-primary/20 rounded-tl-sm"
                        : "bg-white border border-border text-foreground rounded-tl-sm"
                    }`}>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    <p className="text-[10px] text-muted-foreground">{formatTime(msg.created_at)}</p>
                  </div>
                );
              })
            )}
            <div ref={msgEndRef} />
          </div>

          {/* Composer */}
          {activeThread.status !== "encerrado" ? (
            <div className="pt-3 border-t border-border flex-shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
                  }}
                  placeholder={canRespond ? "Escreva seu parecer..." : "Adicione informações ao caso..."}
                  rows={2}
                  className="flex-1 border border-border rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="message-composer"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="h-10 w-10 flex items-center justify-center bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-all disabled:opacity-60 flex-shrink-0"
                  data-testid="send-message-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Enter para enviar · Shift+Enter para nova linha</p>
            </div>
          ) : (
            <div className="pt-3 border-t border-border text-center flex-shrink-0">
              <p className="text-sm text-muted-foreground">Esta interconsulta foi encerrada.</p>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // ===== LIST VIEW =====
  return (
    <Layout>
      <div data-testid="teleconsultation-page" className="space-y-5 max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>Interconsulta</h1>
            <p className="text-sm text-muted-foreground">Discussões clínicas assíncronas com especialistas</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-all"
            data-testid="new-teleconsult-btn"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nova Consulta</span>
          </button>
        </div>

        {/* Status filter chips */}
        <div className="flex gap-2 overflow-x-auto pb-1 animate-fade-in">
          {[["", "Todas"], ["pendente", "Pendentes"], ["em_analise", "Em análise"], ["respondido", "Respondidas"], ["encerrado", "Encerradas"]].map(([val, lbl]) => (
            <button
              key={val}
              onClick={() => setStatusFilter(val)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                statusFilter === val ? "bg-primary text-primary-foreground" : "bg-white border border-border text-muted-foreground hover:bg-secondary"
              }`}
              data-testid={`filter-${val || "all"}`}
            >
              {lbl}
              {val === "" && consultations.filter(c => c.status === "pendente").length > 0 && (
                <span className="ml-1 bg-destructive text-white text-[10px] px-1.5 rounded-full">
                  {consultations.filter(c => c.status === "pendente").length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-24 rounded-xl bg-white border border-border animate-pulse" />)}
          </div>
        ) : consultations.length === 0 ? (
          <div className="bg-white rounded-xl border border-border p-12 text-center animate-fade-in">
            <MessageSquare className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-foreground mb-1" style={{ fontFamily: "Manrope" }}>
              {statusFilter ? "Nenhuma consulta neste status" : "Nenhuma interconsulta ainda"}
            </h3>
            <p className="text-sm text-muted-foreground">Solicite uma interconsulta com um especialista</p>
          </div>
        ) : (
          <div className="space-y-3">
            {consultations.map(c => {
              const status = STATUS_CONFIG[c.status];
              return (
                <button
                  key={c.consult_id}
                  onClick={() => openThread(c)}
                  className="w-full bg-white rounded-xl border border-border p-4 text-left hover:border-primary/40 hover:shadow-sm transition-all animate-fade-in group"
                  data-testid={`consult-row-${c.consult_id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${status?.dot}`} />
                      <p className="text-sm font-semibold text-foreground truncate">{c.patient_name || "Paciente"}</p>
                      {c.wound_info?.wound_type && (
                        <span className="text-xs text-muted-foreground">{WOUND_LABELS[c.wound_info.wound_type]}</span>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${status?.cls}`}>
                        {status?.label}
                      </span>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${PRIORITY_CONFIG[c.priority]?.cls}`}>
                        {PRIORITY_CONFIG[c.priority]?.label}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{c.question}</p>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(c.created_at).toLocaleDateString("pt-BR")}
                      </span>
                      <span>por {c.requesting_user_name}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-primary transition-all" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* New consultation modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-4"
          onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-foreground" style={{ fontFamily: "Manrope" }}>Nova Interconsulta</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-secondary">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Paciente *</label>
                <select value={newConsult.patient_id} onChange={e => handlePatientChange(e.target.value)}
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="teleconsult-patient-select" required>
                  <option value="">Selecionar paciente</option>
                  {patients.map(p => <option key={p.patient_id} value={p.patient_id}>{p.name}</option>)}
                </select>
              </div>
              {wounds.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Lesão</label>
                  <select value={newConsult.wound_id} onChange={e => setNewConsult(p => ({ ...p, wound_id: e.target.value }))}
                    className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                    data-testid="teleconsult-wound-select">
                    <option value="">Selecionar lesão</option>
                    {wounds.map(w => <option key={w.wound_id} value={w.wound_id}>{WOUND_LABELS[w.wound_type]} — {w.location}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Prioridade</label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {Object.entries(PRIORITY_CONFIG).map(([val, cfg]) => (
                    <label key={val}
                      className={`flex items-center justify-center py-2.5 border rounded-lg cursor-pointer text-xs font-medium transition-all ${
                        newConsult.priority === val ? `${cfg.cls}` : "border-border text-muted-foreground hover:bg-secondary"
                      }`}
                      data-testid={`priority-${val}`}
                    >
                      <input type="radio" value={val} checked={newConsult.priority === val}
                        onChange={() => setNewConsult(p => ({ ...p, priority: val }))} className="sr-only" />
                      {cfg.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Descrição do Caso / Dúvida Clínica *</label>
                <textarea value={newConsult.question} onChange={e => setNewConsult(p => ({ ...p, question: e.target.value }))}
                  placeholder="Descreva o caso, evolução clínica e a dúvida específica que precisa de parecer especializado..."
                  rows={5} required
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="teleconsult-question-textarea" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 py-3 border border-border rounded-xl text-sm font-medium hover:bg-secondary">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-medium disabled:opacity-60 hover:bg-primary/90 transition-all"
                  data-testid="submit-teleconsult-btn">
                  {saving ? "Enviando..." : "Abrir Interconsulta"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
